import { Box, Stack, Typography } from '@mui/material'
import { FC, useRef } from 'react'
import { Swiper, SwiperClass, SwiperRef, SwiperSlide } from 'swiper/react'
import { Fade, IconButton } from 'utoniq-ui'

import {
  GachasDetailConnectionFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { useTranslation } from '@/core'
import { Cell } from '@/core/architecture'

type Props = {
  initialSlide: number
  gachas: Maybe<GachasDetailConnectionFragment>
  onAfterChange: (currentSlide: number) => void
}

/**
 * @description
 * ガチャ詳細のカルーセル箇所の表示
 */
export const GachaSliderCell: Cell<Props> = ({
  initialSlide,
  gachas,
  onAfterChange,
}) => {
  const { t } = useTranslation('gachaDetail')
  const swiperRef = useRef<SwiperRef>(null)

  if (!gachas?.edges?.length || initialSlide === undefined) {
    return null
  }

  return (
    <Fade in>
      <Swiper
        ref={swiperRef}
        loop={false}
        slidesPerView={1}
        centeredSlides
        spaceBetween={0}
        initialSlide={initialSlide}
        enabled={gachas.edges.length > 1}
        onSwiper={() => onAfterChange(initialSlide)}
        onSlideChange={(swiper: SwiperClass) =>
          onAfterChange(swiper.activeIndex)
        }
      >
        {gachas.edges.map(({ node }, index) => (
          <SwiperSlide key={index}>
            <Stack
              key={node.id}
              sx={{
                height: 'calc(100vw * 3 / 4)',
                maxHeight: 'calc(600px * 3 / 4)',
                width: '100%',
                backgroundImage: `url("${node?.thumbnail.mediumUri ?? ''}")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize: 'cover',
              }}
            />
            {node.isSoldOut && (
              <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                <Typography
                  variant="overline"
                  color="white"
                  sx={{
                    bgcolor: 'error.main',
                    borderRadius: 1,
                    py: 2,
                    px: '10px',
                  }}
                >
                  {t('soldOutLabel')}
                </Typography>
              </Box>
            )}
          </SwiperSlide>
        ))}
        {(swiperRef.current?.swiper?.activeIndex || 0) > 0 && (
          <PrevArrow onClick={() => swiperRef?.current?.swiper.slidePrev()} />
        )}
        {(swiperRef.current?.swiper.activeIndex || 0) <
          gachas.edges.length - 1 && (
          <NextArrow onClick={() => swiperRef?.current?.swiper.slideNext()} />
        )}
      </Swiper>
    </Fade>
  )
}

type ArrowProps = {
  onClick?: () => void
}

const NextArrow: FC<ArrowProps> = props => (
  <IconButton.ChevronRight
    sx={{
      position: 'absolute',
      top: 'calc(50% - 15px)',
      right: 0,
      zIndex: 2,
      svg: {
        fontSize: '52px',
        color: 'white',
      },
    }}
    {...props}
  />
)

const PrevArrow: FC<ArrowProps> = props => (
  <IconButton.ChevronLeft
    sx={{
      position: 'absolute',
      top: 'calc(50% - 15px)',
      left: 0,
      zIndex: 2,
      svg: {
        fontSize: '52px',
        color: 'white',
      },
    }}
    {...props}
  />
)
